import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceValue, useEffectOnce } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

import { ModalComponentBaseProps, useToast } from '@pcmp/react-providers';
import { Sheets, ModalHeader, ModalBody, ModalFooter } from '@ingka/modal';
import InlineMessage from '@ingka/inline-message';
import InformationIcon from '@ingka/ssr-icon/paths/information-circle';
import ListView, { ListViewItem } from '@ingka/list-view';
import Search from '@ingka/search';

import { useTrackContent } from '@/lib/hooks/tracking';
import Button from '@ingka/button';
import { usePutV2QrCodesBySlugMutation } from '@/redux/apis/codes-api';
import { useGetV1UsersMeQuery, User } from '@/redux/apis/frontend-api';
import {
  serializePermissions,
  serializeUser,
} from '@/lib/schemas/create-code-form/serialization';
import { CodeV2 as Code } from '@/redux/apis/codes-api';
import { normalizeForm } from '@/lib/schemas/create-code-form/normalization';
import {
  useFindCoWorkers,
  useGetUserProfileImageQuery,
} from '@/redux/graph/hooks';
import { getUserProfileImage } from '@/lib/utils/user';
import { LoadingState } from '@/components/helpers/loading';

type ChangeOwnerSiderProps = ModalComponentBaseProps & {
  code: Code;
  previousOwner: string;
};

type UserListItemProps = {
  entry: {
    id: string;
    mail: string;
    displayName?: string;
  };
  selectedUser?: User;
  onUserSelect: (user: User) => void;
};

const UserListItem: FC<UserListItemProps> = ({
  entry,
  selectedUser,
  onUserSelect,
}) => {
  const { data: imageUrl } = useGetUserProfileImageQuery({ email: entry.mail });

  return (
    <ListViewItem
      key={entry.id}
      emphasised={true}
      control="radiobutton"
      title={entry.displayName}
      description={entry.mail}
      image={imageUrl || getUserProfileImage(entry.mail)}
      imageAltText={entry.displayName}
      imageProps={{ className: 'rounded-full' }}
      onChange={() => {
        onUserSelect(mapGraphUser(entry));
      }}
      controlProps={{
        value: entry.mail,
        checked: !!selectedUser && selectedUser.username === entry.mail,
      }}
      aria-label={entry.displayName}
    />
  );
};

const mapGraphUser = (entry: {
  mail: string;
  displayName?: string;
  givenName?: string;
  surname?: string;
  officeLocation?: string;
  country?: string;
  jobTitle?: string;
  manager?: { mail: string };
}): User => ({
  username: entry.mail,
  displayName: entry.displayName || entry.mail,
  givenName: entry.givenName || '',
  surname: entry.surname || '',
  fullName:
    `${entry.givenName || ''} ${entry.surname || ''}`.trim() ||
    entry.displayName ||
    entry.mail,
  cdsLocation: entry.officeLocation || '',
  companyName: '',
  country: entry.country || '',
  jobTitle: entry.jobTitle || '',
  manager: entry.manager?.mail,
});

const UserPicker: FC<{
  search: string;
  selectedUser?: User;
  onUserSelect: (user: User) => void;
  onClearSelection?: () => void;
  autoSelectEmail?: string;
}> = ({
  search,
  selectedUser,
  onUserSelect,
  onClearSelection,
  autoSelectEmail,
}) => {
  // Hooks
  const { error, data: users, isLoading } = useFindCoWorkers(search);
  const { t } = useTranslation();
  // Clear selection if selected user is no longer in the list
  useEffect(() => {
    if (!selectedUser || isLoading || !users) return;
    const isSelectedUserInList = users.some(
      (u) => u.mail === selectedUser.username
    );
    if (!isSelectedUserInList && onClearSelection) {
      onClearSelection();
    }
  }, [users, selectedUser, isLoading, onClearSelection]);

  useEffect(() => {
    if (!autoSelectEmail || isLoading || !users?.length) return;
    const match = users.find((u) => u.mail === autoSelectEmail);
    if (match) {
      onUserSelect(mapGraphUser(match));
    }
  }, [autoSelectEmail, isLoading, onUserSelect, users]);

  if (!search) return null;
  if (isLoading) return <LoadingState />;
  if (error)
    return (
      <InlineMessage
        ssrIcon={InformationIcon}
        title={t('page.changeOwner.messages.errorFindingUsers')}
        variant="negative"
      />
    );

  return (
    <ListView id="userList">
      {users.map((entry) => (
        <UserListItem
          key={entry.id}
          entry={entry}
          selectedUser={selectedUser}
          onUserSelect={onUserSelect}
        />
      ))}
    </ListView>
  );
};

const useChangeOwnerTracking = () => {
  const [trackContent] = useTrackContent();

  useEffectOnce(() => {
    trackContent({ content: 'change-owner', type: 'sider' });
  });
};

export const ChangeOwnerSider: FC<ChangeOwnerSiderProps> = ({
  handleDismiss,
  code,
  previousOwner,
  ...rest
}) => {
  // Hooks
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const [updateQrCode] = usePutV2QrCodesBySlugMutation();
  const { data: userData } = useGetV1UsersMeQuery();
  const currentUser = userData?.username || '';

  // State
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch] = useDebounceValue(inputValue, 300);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pendingAutoSelect, setPendingAutoSelect] = useState(false);

  const handleUserSelect = useCallback((user: User) => {
    setSelectedUser(user);
    setPendingAutoSelect(false);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedUser(null);
  }, []);

  // Track content
  useChangeOwnerTracking();

  // Event handlers
  const handleOwnerChange = async () => {
    if (!code.slug) return; // should not happen, but TS needs this check
    if (selectedUser) {
      const request = {
        slug: code.slug,
        updateCodeV2: {
          user: serializeUser(selectedUser),
          permissions: serializePermissions(normalizeForm(code), selectedUser), // When changing owner, we set permissions to private by default. The new owner can then update the permissions as needed.
        },
      };
      const response = await updateQrCode(request);
      const isError = response && 'error' in response;
      const status = isError ? 'error' : 'success';
      const message = t(`page.changeOwner.messages.${status}`, {
        email: selectedUser?.username,
      });
      // @ts-ignore
      pushToast({ message });
      if (!isError) {
        handleDismiss();
      }
    } else {
      const message = t(`page.changeOwner.messages.userNotFound`);
      // @ts-ignore
      pushToast({ message });
    }
  };

  return (
    <Sheets
      {...rest}
      alignment="right"
      aria-label={t('page.changeOwner.title')}
      footer={
        <ModalFooter>
          <Button
            type="secondary"
            onClick={handleDismiss}
            text={t('label.cancel')}
          />
          <Button
            type="primary"
            text={t('label.apply')}
            onClick={handleOwnerChange}
            disabled={!selectedUser || selectedUser.username === previousOwner}
          />
        </ModalFooter>
      }
      header={
        <ModalHeader
          title={t('page.changeOwner.title')}
          ariaCloseTxt={t('label.close')}
        />
      }
      size="small"
    >
      <ModalBody data-testid="changeOwnerSider">
        <p>{t('page.changeOwner.description')}</p>

        <div className="my-4">
          <Search
            ariaLabel={t('aria.search')}
            clearBtnText={t('aria.clearSearch')}
            id="search"
            onSearch={(_, context) => {
              setInputValue(context.value || '');
            }}
            placeholder={t('label.search')}
            onChange={(e) => {
              setInputValue(e.target.value || '');
            }}
            submitBtnText={t('aria.submit')}
            value={inputValue}
            onClear={() => {
              setInputValue('');
              setPendingAutoSelect(false);
            }}
          />
        </div>

        {currentUser !== previousOwner && (
          <Button
            type="tertiary"
            onClick={() => {
              setInputValue(currentUser);
              setPendingAutoSelect(true);
            }}
            text={t('page.changeOwner.assignToMe')}
            size="xsmall"
            className="mb-4"
          />
        )}
        {/* transfering code to themselves */}
        {selectedUser &&
          selectedUser.username === currentUser &&
          previousOwner !== currentUser && (
            <InlineMessage
              body={t('page.changeOwner.footnote.previousOwner')}
              ssrIcon={InformationIcon}
              variant="informative"
              className="mb-4"
            />
          )}
        {/* transfering code to someone else */}
        {selectedUser &&
          selectedUser.username !== currentUser &&
          selectedUser.username !== previousOwner && (
            <InlineMessage
              body={t('page.changeOwner.footnote.newOwner')}
              ssrIcon={InformationIcon}
              variant="informative"
              className="mb-4"
            />
          )}

        <UserPicker
          search={debouncedSearch}
          selectedUser={selectedUser || undefined}
          onUserSelect={handleUserSelect}
          onClearSelection={handleClearSelection}
          autoSelectEmail={pendingAutoSelect ? currentUser : undefined}
        />
      </ModalBody>
    </Sheets>
  );
};
